// SelectionToolbar — the select→annotate floating micro-toolbar, LIVE.
//
// Promoted from `sketches/source-view-annotation/SelectionToolbar.tsx`
// (.claude/analysis/pdf-source-view-sota.md §3.1, the #1-ranked SOTA
// adoption). Trimmed to the three actions this build wires end to end —
// **Highlight** (instant, no composer), **Comment** (a small inline
// composer), **Add to context** (the LiquidText-job translation, §4.3) —
// dropping the sketch's disabled Correction icon: Correction has no
// `PlaceCorrection` command in core yet (annotation-taxonomy.md §4), and
// a permanently-disabled button earns no place in a shipped surface (the
// owner's SOTA-doc scoping already names Correction as deferred, not
// "half-built here").
//
// Two toolbar STATES, one component: `mode: "actions"` (the icon row) and
// `mode: "comment"` (the inline composer) — mirrors the sketch's
// idle→composing→placed walk.
//
// A11Y: a labelled toolbar (`role="toolbar"`) so assistive tech announces
// it as one unit on open; focus moves to the first action (Highlight —
// never a compose-first jump); Escape reports `onDismiss`.

export type { SelectionToolbarMode, SelectionToolbarProps };
export { SelectionToolbar };

import { Trans, useLingui } from "@lingui/react/macro";
import { type KeyboardEvent, useEffect, useRef } from "react";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Text } from "../Text/Text.js";
import { Textarea } from "../Textarea/Textarea.js";
import { Tooltip, TooltipProvider } from "../Tooltip/Tooltip.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import "./SourceView.css";

type SelectionToolbarMode = "actions" | "comment";

type SelectionToolbarProps = {
    readonly mode: SelectionToolbarMode;
    readonly busy: boolean;
    readonly onHighlight: () => void;
    readonly onStartComment: () => void;
    readonly commentDraft: string;
    readonly onCommentDraftChange: (value: string) => void;
    readonly onPostComment: () => void;
    readonly onCancelComment: () => void;
    readonly onAddToContext: () => void;
    readonly onDismiss: () => void;
};

const SelectionToolbar = ({
    mode,
    busy,
    onHighlight,
    onStartComment,
    commentDraft,
    onCommentDraftChange,
    onPostComment,
    onCancelComment,
    onAddToContext,
    onDismiss,
}: SelectionToolbarProps) => {
    const { t } = useLingui();
    const firstActionRef = useRef<HTMLButtonElement>(null);

    // Focus the first action when the actions row mounts/re-mounts — the
    // gesture is "select, then the toolbar is immediately keyboard-ready".
    useEffect(() => {
        if (mode === "actions") {
            firstActionRef.current?.focus();
        }
    }, [mode]);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape") {
            event.stopPropagation();
            onDismiss();
        }
    };

    return (
        <TooltipProvider>
            <div
                className="bilda-source-view__selection-toolbar"
                role="toolbar"
                aria-label={t`Annotate selection`}
                onKeyDown={handleKeyDown}
                // Selecting inside the toolbar itself must not clear the
                // host's tracked DOM selection before a click handler runs.
                onMouseDown={(e) => e.preventDefault()}
            >
                {mode === "actions" ? (
                    <div className="bilda-source-view__selection-toolbar-row">
                        <Tooltip content={t`Highlight – instant, no note`}>
                            <IconButton
                                ref={firstActionRef}
                                label={t`Highlight`}
                                variant="ghost"
                                size="sm"
                                disabled={busy}
                                onClick={onHighlight}
                            >
                                <Icon name="star" size="sm" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip
                            content={t`Comment – write a note on this passage`}
                        >
                            <IconButton
                                label={t`Comment`}
                                variant="ghost"
                                size="sm"
                                disabled={busy}
                                onClick={onStartComment}
                            >
                                <Icon name="chat" size="sm" />
                            </IconButton>
                        </Tooltip>
                        <div
                            className="bilda-source-view__selection-toolbar-divider"
                            aria-hidden="true"
                        />
                        <Tooltip
                            content={t`Add to context – bring this passage into the assistant's context`}
                        >
                            <IconButton
                                label={t`Add to context`}
                                variant="ghost"
                                size="sm"
                                disabled={busy}
                                onClick={onAddToContext}
                            >
                                <Icon name="pin" size="sm" />
                            </IconButton>
                        </Tooltip>
                    </div>
                ) : (
                    <div className="bilda-source-view__selection-toolbar-composer">
                        <Text
                            as="label"
                            size="xs"
                            weight="medium"
                            htmlFor="bilda-source-view-comment-draft"
                        >
                            <Trans>Comment on this passage</Trans>
                        </Text>
                        <Textarea
                            id="bilda-source-view-comment-draft"
                            size="sm"
                            rows={2}
                            autoFocus
                            placeholder={t`Say something about this passage…`}
                            value={commentDraft}
                            onChange={(event) =>
                                onCommentDraftChange(event.target.value)
                            }
                        />
                        <div className="bilda-source-view__selection-toolbar-composer-actions">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onCancelComment}
                            >
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                disabled={
                                    busy || commentDraft.trim().length === 0
                                }
                                onClick={onPostComment}
                            >
                                <Trans>Post</Trans>
                            </Button>
                        </div>
                    </div>
                )}
                <VisuallyHidden>
                    <span aria-live="polite">
                        {mode === "actions"
                            ? t`Selection toolbar open: Highlight, Comment, Add to context.`
                            : t`Writing a comment on the selected passage.`}
                    </span>
                </VisuallyHidden>
            </div>
        </TooltipProvider>
    );
};
